// @flow
import React from "react";
import { t, ngettext, msgid } from "ttag";
import {
    BuildingCommunity,
    Bed,
    EPCLabelImage,
    BoxAlignBottom,
    SurfaceHabitable,
    Bath,
    Icon,
} from "@2po-c21/components";
import { energyUtils } from "@utils";
import style from "./style.module.scss";

type Props = {
    property: {
        numberOfFacades?: number,
        surface?: {
            totalSurfaceArea?: {
                unit: string,
                value: number,
            },
            surfaceAreaGarden?: {
                unit: string,
                value: number,
            },
        },
        rooms?: {
            numberOfBedrooms?: number,
            numberOfBathrooms?: number,
        },
        energySpecifications?: {
            energyLabel?: string,
            energyCertificate?: "PEB" | "EPC",
        },
        address?: {
            region?: "FLANDERS" | "WALLONIA" | "BRUSSELS",
        },
    },
    locale: string,
};

const PropertyFeatures = ({ property, locale }: Props) => {
    const energySpecificationsConverter = energyUtils.unitsConverter;

    const renderNumberOfFacades = (numberOfFacades) => {
        if (!numberOfFacades || numberOfFacades === 0) return null;
        return (
            <div className={style.propertyDetailsItem}>
                <Icon icon={BuildingCommunity} />
                {numberOfFacades}
                <span className={style.propertyDetailsItemText}>
                    {t`property-details.property-description.facades`}
                </span>
            </div>
        );
    };

    const renderNumberOfBedrooms = (numberOfBedrooms) => {
        if (!numberOfBedrooms || numberOfBedrooms === 0) return null;
        return (
            <div className={style.propertyDetailsItem}>
                <Icon icon={Bed} />
                {numberOfBedrooms}
                <span className={style.propertyDetailsItemText}>
                    {ngettext(
                        msgid`property-details.banner-icon.bedroom`,
                        `property-details.banner-icon.bedrooms`,
                        numberOfBedrooms,
                    )}
                </span>
            </div>
        );
    };

    const renderSurfaceArea = (surfaceArea) => {
        if (!surfaceArea?.value || surfaceArea.value === 0) return null;
        return (
            <div className={style.propertyDetailsItem}>
                <Icon icon={SurfaceHabitable} />
                {energySpecificationsConverter[surfaceArea.unit](
                    surfaceArea.value,
                )}
                <span className={style.propertyDetailsItemText}>
                    {t`property-details.banner-icon.total-surface-area`}
                </span>
            </div>
        );
    };

    const renderGardenSurface = (gardenSurface) => {
        if (!gardenSurface?.value || gardenSurface.value === 0) return null;
        return (
            <div className={style.propertyDetailsItem}>
                <Icon icon={BoxAlignBottom} />
                {energySpecificationsConverter[gardenSurface.unit](
                    gardenSurface.value,
                )}
                <span className={style.propertyDetailsItemText}>
                    {t`property-details.banner-icon.surface-area-garden`}
                </span>
            </div>
        );
    };

    const renderNumberOfBathrooms = (numberOfBathrooms) => {
        if (!numberOfBathrooms || numberOfBathrooms === 0) return null;
        return (
            <div className={style.propertyDetailsItem}>
                <Icon icon={Bath} />
                {numberOfBathrooms}
                <span className={style.propertyDetailsItemText}>
                    {ngettext(
                        msgid`property-details.banner-icon.bathroom`,
                        `property-details.banner-icon.bathrooms`,
                        numberOfBathrooms,
                    )}
                </span>
            </div>
        );
    };

    return (
        <div className={style.propertyDetailsItems}>
            {renderNumberOfFacades(property?.numberOfFacades)}
            {renderSurfaceArea(property?.surface?.totalSurfaceArea)}
            {renderNumberOfBedrooms(property?.rooms?.numberOfBedrooms)}
            {renderGardenSurface(property?.surface?.surfaceAreaGarden)}
            {renderNumberOfBathrooms(property?.rooms?.numberOfBathrooms)}
            {property?.energySpecifications?.energyLabel && (
                <div>
                    <EPCLabelImage
                        label={property.energySpecifications.energyLabel}
                        certificate={
                            property.energySpecifications.energyCertificate
                        }
                        region={property.address.region}
                        locale={locale}
                    />
                </div>
            )}
        </div>
    );
};

export default PropertyFeatures;
